// Pure judge-alignment scorecard helpers (alignment-scorecard.md module plan,
// UI Layering Sweep Phase 3): the `?cell=<human>-<machine>` drill-param codec,
// server-paged drill selection,
// the uncapped expected-count disclosure, and the κ / rate formatting shared
// by the rollup and detail panes.
import type { components } from "../api/schema";
import type { ConfusionMatrixSelection } from "../ui";
import { formatPercent } from "./dashboardMetrics";
import { isApiRequestError } from "./errors";
import type { AlignmentCellParam } from "./routerSearch";
import { shortResourceId } from "./shellNavigation";

type AlignmentConfusionCell = components["schemas"]["AlignmentConfusionCell"];
type AlignmentScoreResponse = components["schemas"]["AlignmentScoreResponse"];
type AlignmentScorecardShape = components["schemas"]["AlignmentScorecard"];

// Server-side page size for the disagreement drill (`disagreement_limit`).
export const DISAGREEMENT_LIMIT = 50;

// Serialize a matrix-cell selection into the `?cell=` search param. The
// off-diagonal constraint is enforced where selections originate (the
// ConfusionMatrix primitive only raises off-diagonal cells) and where params
// enter (routerSearch strips diagonal values).
export function cellParam(
  selection: ConfusionMatrixSelection,
): AlignmentCellParam {
  return `${selection.human}-${selection.machine}`;
}

// Parse a validated `?cell=` param back into a matrix selection. The param is
// already shape-checked by `parseAlignmentSearch`; absent stays null.
export function parseCellParam(
  param: AlignmentCellParam | undefined,
): ConfusionMatrixSelection | null {
  if (param === undefined) {
    return null;
  }
  const [human, machine] = param.split("-") as [
    ConfusionMatrixSelection["human"],
    ConfusionMatrixSelection["machine"],
  ];
  return { human, machine };
}

/**
 * How many disagreement rows the current drill SHOULD contain, derived from
 * the confusion-matrix counts. Drill pages are additionally team-scoped,
 * so the rendered table can fall short of the aggregate cell count.
 */
export function expectedDisagreementCount(
  response: AlignmentScoreResponse,
  selectedCell: ConfusionMatrixSelection | null,
  selectedMarkerId: string | null,
): number | null {
  const scorecard =
    selectedMarkerId === null
      ? response.overall
      : ((response.by_marker ?? []).find(
          (entry) => entry.marker_id === selectedMarkerId,
        ) ?? null);
  if (scorecard === null) {
    return null;
  }
  const confusion: AlignmentConfusionCell[] = scorecard.confusion ?? [];
  if (selectedCell !== null) {
    return (
      confusion.find(
        (cell) =>
          cell.human === selectedCell.human &&
          cell.machine === selectedCell.machine,
      )?.count ?? 0
    );
  }
  return confusion
    .filter((cell) => cell.human !== cell.machine)
    .reduce((sum, cell) => sum + cell.count, 0);
}

export function drillLabel(
  selectedCell: ConfusionMatrixSelection | null,
  selectedMarkerId: string | null,
): string {
  const parts: string[] = [];
  if (selectedMarkerId !== null) {
    parts.push(`Marker ${shortResourceId(selectedMarkerId)}`);
  }
  if (selectedCell !== null) {
    parts.push(`Human ${selectedCell.human} / Machine ${selectedCell.machine}`);
  }
  return parts.join(" · ");
}

export function failClass(scorecard: {
  per_class?: AlignmentScorecardShape["per_class"];
}): NonNullable<AlignmentScorecardShape["per_class"]>[number] {
  return (
    scorecard.per_class?.find((entry) => entry.verdict === "fail") ?? {
      precision: null,
      recall: null,
      support_human: 0,
      support_machine: 0,
      verdict: "fail",
    }
  );
}

export function formatRate(value: number | null): string {
  return value === null ? "--" : formatPercent(value * 100);
}

export function formatKappa(value: number | null): string {
  if (value === null || !Number.isFinite(value)) {
    return "--";
  }
  return value.toLocaleString(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
}

export function kappaBand(value: number | null): string {
  if (value === null) {
    return "κ undefined";
  }
  if (value <= 0) {
    return "Less than chance";
  }
  if (value <= 0.2) {
    return "Slight";
  }
  if (value <= 0.4) {
    return "Fair";
  }
  if (value <= 0.6) {
    return "Moderate";
  }
  if (value <= 0.8) {
    return "Substantial";
  }
  return "Almost perfect";
}

// The page-level score-query error line (`code: message` for Status
// envelopes, message text otherwise).
export function scoreErrorMessage(error: unknown): string | null {
  if (error == null) {
    return null;
  }
  if (isApiRequestError(error)) {
    return `${error.status.code}: ${error.status.message}`;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Request failed.";
}

export function kappaCaption(scorecard: AlignmentScorecardShape): string {
  if (scorecard.single_class) {
    return "κ undefined";
  }
  const band = kappaBand(scorecard.cohen_kappa);
  return scorecard.low_sample ? `Low sample · ${band}` : band;
}
